import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  Robot, Play, Clock, CheckCircle, XCircle, ArrowClockwise,
  Lightning, Bell, CalendarBlank, DeviceMobile, UsersThree,
  Power, Pause, CaretDown, CaretUp
} from '@phosphor-icons/react';

const JOB_INFO = {
  generate_recurring: { label: 'Recurring Tasks', desc: 'Auto-creates daily/weekly/monthly tasks at midnight', icon: CalendarBlank, color: '#FF6B35' },
  morning_reminder: { label: 'Morning Reminder', desc: 'Sends task summary every morning at 7 AM', icon: Bell, color: '#22C55E' },
  evening_reminder: { label: 'Evening Reminder', desc: 'Reminds about pending tasks at 6 PM', icon: Bell, color: '#F59E0B' },
  screen_time_check: { label: 'Screen Time Check', desc: 'Monitors and enforces device limits every 30 min', icon: DeviceMobile, color: '#00E5FF' },
  daily_reset: { label: 'Daily Reset', desc: 'Resets device timers and unlocks at midnight', icon: ArrowClockwise, color: '#A855F7' },
  auto_attendance: { label: 'Auto Attendance', desc: 'Marks absent staff at 10 PM', icon: UsersThree, color: '#EC4899' },
  smart_alerts: { label: 'Smart Alerts', desc: 'Warranty, balance, birthday & medicine alerts at 8 AM', icon: Lightning, color: '#EF4444' },
};

export default function AutomationPage() {
  const { api } = useAuth();
  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState('');
  const [showLogs, setShowLogs] = useState(false);
  const [settings, setSettings] = useState({});

  const fetchData = async () => {
    try {
      const [statusRes, logsRes] = await Promise.all([
        api.get('/scheduler/status'),
        api.get('/scheduler/logs?limit=30')
      ]);
      setStatus(statusRes.data);
      setLogs(logsRes.data);
      setSettings(statusRes.data.automation_settings || {});
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, [api]);

  const triggerJob = async (jobId) => {
    setTriggering(jobId);
    try {
      const res = await api.post(`/scheduler/trigger/${jobId}`);
      alert(res.data.message);
      fetchData();
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
    setTriggering('');
  };

  const toggleJob = async (settingKey, value) => {
    try {
      await api.put('/scheduler/settings', { [settingKey]: value });
      setSettings(prev => ({ ...prev, [settingKey]: value }));
      fetchData();
    } catch (e) { console.error(e); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="automation-page" className="space-y-8 relative">
      <div className="glow-orb-orange -top-20 -left-20 z-0" />
      <div className="glow-orb-teal top-60 right-0 z-0" />

      {/* Header */}
      <div className="relative z-10 animate-fade-up flex items-start justify-between">
        <div>
          <p className="text-neutral-500 text-sm font-medium">Background Tasks</p>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-white tracking-tight mt-1">Automation</h1>
          <p className="text-neutral-500 text-sm mt-1">AI manages your household on autopilot</p>
        </div>
        <div className="flex items-center gap-3">
          <div className={`flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold ${
            status?.running ? 'bg-green-500/10 text-green-400 border border-green-500/20' : 'bg-red-500/10 text-red-400 border border-red-500/20'
          }`} data-testid="scheduler-status">
            <Power size={14} weight="fill" />
            {status?.running ? 'Scheduler Active' : 'Scheduler Offline'}
          </div>
        </div>
      </div>

      {/* Jobs Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 relative z-10">
        {status?.jobs?.map((job, idx) => {
          const info = JOB_INFO[job.id] || { label: job.name, desc: '', icon: Lightning, color: '#737373' };
          const Icon = info.icon;
          const settingKey = `${job.id}_enabled`;
          const isEnabled = settings[settingKey] !== false;
          const isPaused = job.next_run === 'paused';

          return (
            <div key={job.id} className="glass-card p-5 animate-fade-up" data-testid={`job-${job.id}`}
              style={{ animationDelay: `${100 + idx * 60}ms` }}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-2xl flex items-center justify-center" style={{ backgroundColor: `${info.color}15` }}>
                    <Icon size={20} weight="duotone" style={{ color: info.color }} />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-white text-sm">{info.label}</h3>
                    <p className="text-[10px] text-neutral-500">{info.desc}</p>
                  </div>
                </div>
              </div>

              {/* Schedule info */}
              <div className="flex items-center gap-2 mb-4 text-[11px]">
                <Clock size={12} className="text-neutral-500" />
                <span className="text-neutral-400">
                  {isPaused ? 'Paused' : `Next: ${new Date(job.next_run).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', day: 'numeric', month: 'short' })}`}
                </span>
              </div>

              {/* Controls */}
              <div className="flex items-center gap-2">
                <button onClick={() => triggerJob(job.id)} disabled={triggering === job.id}
                  data-testid={`trigger-${job.id}`}
                  className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-xs text-white font-medium hover:bg-white/10 transition-colors disabled:opacity-50">
                  {triggering === job.id ? (
                    <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Play size={12} weight="fill" />
                  )}
                  Run Now
                </button>
                <button onClick={() => toggleJob(settingKey, !isEnabled)}
                  data-testid={`toggle-${job.id}`}
                  className={`px-3 py-2 rounded-xl text-xs font-medium transition-colors ${
                    isEnabled
                      ? 'bg-green-500/10 text-green-400 border border-green-500/20 hover:bg-green-500/20'
                      : 'bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20'
                  }`}>
                  {isEnabled ? 'ON' : 'OFF'}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Summary */}
      <div className="glass-card p-5 relative z-10 animate-fade-up" style={{ animationDelay: '400ms' }}>
        <div className="flex items-center gap-3">
          <Robot size={24} weight="duotone" className="text-[#FF6B35]" />
          <div>
            <h3 className="font-heading font-semibold text-white text-sm">What HomeCommand Automates</h3>
            <p className="text-neutral-500 text-xs mt-1">All tasks run in the background — no manual intervention needed</p>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-4 text-xs">
          <div className="p-3 rounded-xl bg-white/[0.02] border border-white/5">
            <p className="text-[#FF6B35] font-semibold">12:00 AM</p>
            <p className="text-neutral-400 mt-0.5">Reset devices, generate tasks</p>
          </div>
          <div className="p-3 rounded-xl bg-white/[0.02] border border-white/5">
            <p className="text-[#22C55E] font-semibold">7:00 AM</p>
            <p className="text-neutral-400 mt-0.5">Morning briefing + reminders</p>
          </div>
          <div className="p-3 rounded-xl bg-white/[0.02] border border-white/5">
            <p className="text-[#00E5FF] font-semibold">Every 30 min</p>
            <p className="text-neutral-400 mt-0.5">Screen time enforcement</p>
          </div>
          <div className="p-3 rounded-xl bg-white/[0.02] border border-white/5">
            <p className="text-[#F59E0B] font-semibold">6:00 PM</p>
            <p className="text-neutral-400 mt-0.5">Evening pending tasks alert</p>
          </div>
          <div className="p-3 rounded-xl bg-white/[0.02] border border-white/5">
            <p className="text-[#EC4899] font-semibold">10:00 PM</p>
            <p className="text-neutral-400 mt-0.5">Auto-mark absent staff</p>
          </div>
          <div className="p-3 rounded-xl bg-white/[0.02] border border-white/5">
            <p className="text-[#A855F7] font-semibold">Continuous</p>
            <p className="text-neutral-400 mt-0.5">Bedtime device lockdown</p>
          </div>
          <div className="p-3 rounded-xl bg-white/[0.02] border border-white/5">
            <p className="text-[#EF4444] font-semibold">8:00 AM</p>
            <p className="text-neutral-400 mt-0.5">Warranty, balance, birthday alerts</p>
          </div>
        </div>
      </div>

      {/* Execution Logs */}
      <div className="glass-card relative z-10 animate-fade-up" data-testid="scheduler-logs" style={{ animationDelay: '500ms' }}>
        <button onClick={() => setShowLogs(!showLogs)}
          data-testid="toggle-logs-btn"
          className="w-full p-5 flex items-center justify-between text-left">
          <div className="flex items-center gap-2">
            <Clock size={18} weight="duotone" className="text-neutral-400" />
            <span className="font-heading font-semibold text-white text-sm">Execution Log</span>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-neutral-500">{logs.length} entries</span>
          </div>
          {showLogs ? <CaretUp size={16} className="text-neutral-500" /> : <CaretDown size={16} className="text-neutral-500" />}
        </button>

        {showLogs && (
          <div className="px-5 pb-5 max-h-80 overflow-y-auto space-y-1.5">
            {logs.length === 0 ? (
              <p className="text-neutral-500 text-sm text-center py-4">No executions yet. Jobs will run on schedule.</p>
            ) : (
              logs.map((log, i) => (
                <div key={i} className="flex items-center gap-3 py-2 px-3 rounded-lg bg-white/[0.02] text-xs">
                  {log.status === 'success' ? (
                    <CheckCircle size={14} weight="fill" className="text-green-400 flex-shrink-0" />
                  ) : (
                    <XCircle size={14} weight="fill" className="text-red-400 flex-shrink-0" />
                  )}
                  <span className="text-white font-medium min-w-[140px]">
                    {JOB_INFO[log.job_name]?.label || log.job_name}
                  </span>
                  <span className="text-neutral-400 flex-1 truncate">{log.details}</span>
                  <span className="text-neutral-600 flex-shrink-0">
                    {new Date(log.timestamp).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', day: 'numeric', month: 'short' })}
                  </span>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}
